import { loadSource as decodeSource, disposeSource, type SourceImage } from '@/core/image'
import { PersistenceReadError } from '@/core/persistence/errors'
import {
  evictBeyondLimit,
  getRecentEdit,
  markAllClosed,
  putRecentEdit,
} from '@/core/persistence/recentEdits'
import { recordFromState } from '@/core/persistence/serialize'
import { createThumbnail } from '@/core/persistence/thumbnail'
import { PersistenceStatus, type RecentEditRecord } from '@/core/persistence/types'
import { useEditSession } from '@/core/store/editSession'

export async function persistNewEdit(): Promise<RecentEditRecord> {
  const state = useEditSession.getState()
  state.setPersistenceStatus(PersistenceStatus.Saving)
  try {
    await markAllClosed()
    const current = useEditSession.getState()
    if (!current.document) throw new Error('Persisting a new edit requires a loaded document')
    const thumbnail = await createThumbnail(current.document, current.sources)
    const record = recordFromState(current, thumbnail)
    await putRecentEdit(record)
    await evictBeyondLimit()
    useEditSession.getState().setPersistedEditId(record.id)
    useEditSession.getState().setPersistenceStatus(PersistenceStatus.Saved)
    return record
  } catch (error) {
    useEditSession.getState().setPersistenceStatus(PersistenceStatus.Disabled)
    throw error
  }
}

export async function restoreRecentEdit(id: string): Promise<RecentEditRecord> {
  if (!id.trim()) throw new PersistenceReadError('A recent-edit id is required for restore.')
  useEditSession.getState().setPersistenceStatus(PersistenceStatus.Saving)
  const decodedSources = new Map<RecentEditRecord['sources'][number]['id'], SourceImage>()
  let installed = false
  try {
    const record = await getRecentEdit(id)
    if (!record) throw new PersistenceReadError(`Saved edit "${id}" was not found.`)
    for (const source of record.sources) {
      const decoded = await decodeSource(new File([source.file], source.fileName, { type: source.mimeType }))
      if (decoded.width !== source.width || decoded.height !== source.height) {
        disposeSource(decoded)
        throw new PersistenceReadError(
          `Saved source "${source.fileName}" decoded to ${decoded.width}×${decoded.height}; expected ${source.width}×${source.height}.`,
        )
      }
      decodedSources.set(source.id, decoded)
    }

    useEditSession.getState().restoreSession(
      decodedSources,
      record.document,
      record.selectedNodeId,
      record.exportOptions,
      record.id,
    )
    installed = true
    await markAllClosed()
    const opened = { ...record, isOpen: true, updatedAt: Date.now() }
    await putRecentEdit(opened)
    useEditSession.getState().setPersistenceStatus(PersistenceStatus.Saved)
    return opened
  } catch (error) {
    if (!installed) {
      for (const source of decodedSources.values()) disposeSource(source)
    }
    useEditSession.getState().setPersistenceStatus(PersistenceStatus.Disabled)
    throw error
  }
}
